import React from 'react';
import type { VehicleDetailsEntity } from '@packages/domain/techline';

import { Button, TextInputHelper, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { VehicleCard } from './vin-vehicle-details/vehicle-card';
import { VehicleDetails } from './vin-vehicle-details/vehicle-details';
import colors from '@packages/core/styles/colors';

const MAX_VIN_LENGTH = 17;

const useStyles = createUseStyles({
    divider: {
        height: '1px',
        backgroundColor: colors.grayThree,
    },
});

interface VinVehicleDetailsProps {
    isVinCheckLoading: boolean;
    isVinCheckButtonDisabled: boolean;
    onSelectedVehicle: (vehicle: VehicleDetailsEntity) => void;
    onVehicleDetailsChange: (vehicle: VehicleDetailsEntity | undefined) => void;
    onVinCheck: () => void;
    onVinInputChange: (vin: string) => void;
    possibleVehicles: VehicleDetailsEntity[];
    vehicleDetails: VehicleDetailsEntity | undefined;
    vinError: string;
    vinInputValue: string;
}

export function VinVehicleDetails({
    isVinCheckLoading,
    isVinCheckButtonDisabled,
    onSelectedVehicle,
    onVehicleDetailsChange,
    onVinCheck,
    onVinInputChange,
    possibleVehicles,
    vehicleDetails,
    vinError,
    vinInputValue,
}: VinVehicleDetailsProps) {
    const classes = useStyles();

    const showPossibleVehicles = possibleVehicles.length > 0;
    const showVehicleDetails = vehicleDetails && possibleVehicles.length === 0;

    return (
        <>
            <div>
                <TextInputHelper
                    controlId="vin-input"
                    autoCapitalize="characters"
                    style={{ borderRadius: 4 }}
                    placeholder="Enter VIN"
                    labelStyle={{ fontWeight: 400, paddingBottom: 2 }}
                    label="VIN"
                    labelHint="VIN should be either 8 or 17 characters long"
                    required
                    showCharCount
                    maxLength={MAX_VIN_LENGTH}
                    value={vinInputValue}
                    errorMessage={vinError}
                    onChangeText={(text: string) => {
                        onVinInputChange(text.toUpperCase());
                        onVehicleDetailsChange(undefined);
                    }}
                    rightIcon={({ inputState }) => (
                        <Button
                            isLoading={isVinCheckLoading}
                            onPress={onVinCheck}
                            variant="primary"
                            disabled={isVinCheckButtonDisabled || inputState.isDisabled}
                            style={{
                                height: inputState.hasError || inputState.isFocused ? 36 : 38,
                                marginRight: -13,
                                borderRadius: 4,
                            }}
                        >
                            Check VIN
                        </Button>
                    )}
                />
            </div>
            <div>
                {showVehicleDetails && (
                    <>
                        <div className={`${classes.divider} mt-6`} />
                        <div className="pt-6 pb-2">
                            <Typography variant="h5">Confirm vehicle details</Typography>
                        </div>
                        <div className="row">
                            <VehicleDetails vehicle={vehicleDetails} />
                        </div>
                    </>
                )}

                {showPossibleVehicles && (
                    <>
                        <div className={`${classes.divider} mt-6 mb-6`} />

                        <div className="d-flex flex-column gap-2 pb-4">
                            <Typography>
                                This VIN has {possibleVehicles.length} possible vehicles associated with it.
                            </Typography>
                            <Typography>
                                Please select the correct vehicle for
                                <Typography variant="h6"> VIN {vinInputValue}</Typography>
                            </Typography>
                        </div>
                        <div className="row">
                            {possibleVehicles.map((vehicle: VehicleDetailsEntity, index: number) => (
                                <div key={index} className="col-md-12 col-lg-6 pb-2">
                                    <VehicleCard vehicle={vehicle} onSelect={() => onSelectedVehicle(vehicle)} />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
